import { useState, type FormEvent } from "react";
import { AuthShell } from "../components/AuthShell";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { api, ApiError } from "../lib/api";
import { useMe } from "../lib/me";

const RESERVED = new Set([
  "www", "app", "api", "admin", "auth", "mail", "support", "help", "docs",
  "status", "triagent", "triage", "appgambit", "aws", "inbox", "ses", "test",
  "stage", "prod", "dev",
]);

export default function ClaimSlug() {
  const { refresh } = useMe();
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cleaned = slug.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 32);
  const inboundPreview = cleaned ? `support@${cleaned}.inbox.appgambit.com` : "support@your-slug.inbox.appgambit.com";

  const slugError =
    cleaned.length > 0 && cleaned.length < 3
      ? "Too short — at least 3 characters."
      : RESERVED.has(cleaned)
        ? "That word is reserved. Pick another."
        : !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(cleaned) && cleaned.length > 0
          ? "Use lowercase letters, numbers, and hyphens only. No leading/trailing hyphen."
          : null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (slugError) return;
    setError(null);
    setLoading(true);
    try {
      await api.post("/api/setup/claim-slug", { slug: cleaned, displayName });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't claim slug. Try another.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Claim your inbox" subtitle="Pick a slug for your dedicated email subdomain.">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          name="displayName"
          label="Company or team name"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Acme Corp"
        />
        <Input
          name="slug"
          label="Slug"
          required
          value={cleaned}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="acme"
          hint="3–32 chars, lowercase letters, numbers, and hyphens."
          error={slugError ?? undefined}
        />
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Your inbound address
          </div>
          <div className="mt-0.5 break-all font-mono text-sm text-gray-900">{inboundPreview}</div>
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <Button
          type="submit"
          loading={loading}
          disabled={!cleaned || !!slugError || !displayName}
          className="w-full"
        >
          Claim and continue
        </Button>
      </form>
    </AuthShell>
  );
}
